/**
 * ConfirmationDialog component
 *
 * A reusable confirmation dialog with multiple variants for different use cases.
 * Provides a consistent, accessible way to confirm actions or display alerts.
 */

import * as React from "react"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@shadcn/components/ui/alert-dialog"
import { buttonVariants } from "@shadcn/components/ui/button"
import { cn } from "@shadcn/lib/utils"

export type ConfirmationDialogVariant = "default" | "destructive" | "info"

export interface ConfirmationDialogProps {
	/**
	 * Whether the dialog is open (controlled mode)
	 */
	open: boolean
	/**
	 * Callback when the dialog open state changes (controlled mode)
	 */
	onOpenChange: (open: boolean) => void
	/**
	 * Visual variant of the dialog
	 * @default "default"
	 */
	variant?: ConfirmationDialogVariant
	/**
	 * Title of the dialog
	 */
	title: string
	/**
	 * Description/message to display (supports ReactNode for complex content)
	 */
	description: React.ReactNode
	/**
	 * Text for the confirm button
	 * @default "Confirm"
	 */
	confirmText?: string
	/**
	 * Text for the cancel button
	 * @default "Cancel"
	 */
	cancelText?: string
	/**
	 * Whether to show the cancel button
	 * @default true
	 */
	showCancel?: boolean
	/**
	 * Callback when confirmed
	 */
	onConfirm: () => void
	/**
	 * Callback when cancelled (optional)
	 */
	onCancel?: () => void
	/**
	 * Whether the action is loading
	 * @default false
	 */
	isLoading?: boolean
}

const variantStyles = {
	default: "",
	destructive: buttonVariants({ variant: "destructive" }),
	info: cn(buttonVariants({ variant: "default" }), "bg-info-500 hover:bg-info-500/80 text-white"),
}

/**
 * ConfirmationDialog provides a consistent way to confirm actions or display alerts.
 *
 * @example Default confirmation
 * ```tsx
 * <ConfirmationDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   onConfirm={handleConfirm}
 * />
 * ```
 *
 * @example Destructive action
 * ```tsx
 * <ConfirmationDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   variant="destructive"
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   confirmText="Delete"
 *   onConfirm={handleDelete}
 * />
 * ```
 *
 * @example Alert (no cancel button)
 * ```tsx
 * <ConfirmationDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   variant="info"
 *   title="Validation Errors"
 *   description={<div>Please fix the following errors...</div>}
 *   confirmText="OK"
 *   showCancel={false}
 *   onConfirm={() => setOpen(false)}
 * />
 * ```
 */
export function ConfirmationDialog({
	open,
	onOpenChange,
	variant = "default",
	title,
	description,
	confirmText = "Confirm",
	cancelText = "Cancel",
	showCancel = true,
	onConfirm,
	onCancel,
	isLoading = false,
}: Readonly<ConfirmationDialogProps>) {
	const handleConfirm = () => {
		onConfirm()
	}

	const handleCancel = () => {
		onCancel?.()
		onOpenChange(false)
	}

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div>{description}</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					{showCancel && <AlertDialogCancel disabled={isLoading}>{cancelText}</AlertDialogCancel>}
					<AlertDialogAction
						onClick={(e) => {
							e.preventDefault()
							handleConfirm()
						}}
						disabled={isLoading}
						className={variantStyles[variant]}>
						{confirmText}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
